
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SEOHead } from '@/components/SEOHead';
import { Send, Crown, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';
import { CONTACT_LIMITS, EMAIL_MAX, checkContactInput, type ContactField } from '@/utils/inputChecks';

/** The message under a field the server would refuse (ERI-1559). */
const FieldError: React.FC<{ id: string; message?: string }> = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="mt-1.5 text-sm text-destructive">
      {message}
    </p>
  ) : null;

const Contact: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [loadedAt] = useState(() => Date.now());
  const [fieldError, setFieldError] = useState<{ field: ContactField; message: string } | null>(null);
  const errorFor = (field: ContactField) => (fieldError?.field === field ? fieldError.message : undefined);
  const messageAtLimit = formData.message.length >= CONTACT_LIMITS.message;

  const handlePremiumUpgrade = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    const showCheckoutError = () =>
      toast({
        variant: 'destructive',
        title: 'Could not start checkout',
        description: 'Something went wrong opening the secure checkout page. Please try again.',
      });

    setIsUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { referralId: getRewardfulReferral() }
      });

      if (error || !data?.url) {
        console.error('Error creating checkout:', error ?? 'no checkout URL returned');
        showCheckoutError();
        return;
      }

      redirectToCheckout(data.url, 'Premium');
    } catch (error) {
      console.error('Error creating checkout:', error);
      showCheckoutError();
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Honeypot — if filled, it's a bot
    if (honeypot) {
      // Fake success
      toast({ title: 'Message sent!', description: 'Thanks for reaching out.' });
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
      return;
    }

    // The same checks send-contact-email runs, so the visitor hears why here
    // instead of getting the server's 400.
    const checked = checkContactInput(formData);
    // `=== false`, not `!`: the app compiles without strictNullChecks, where `!` doesn't narrow.
    if (checked.ok === false) {
      setFieldError({ field: checked.field, message: checked.error });
      document.getElementById(checked.field)?.focus();
      setIsSubmitting(false);
      return;
    }
    setFieldError(null);

    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          ...checked.value,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          _formTime: Date.now() - loadedAt,
        }
      });

      if (error) throw error;

      toast({
        title: 'Message sent!',
        description: 'Thanks for reaching out. I\'ll get back to you within 24 hours.',
      });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error sending message',
        description: 'Something went wrong. Please try again or email eric@milkywayplanner.com directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (fieldError?.field === name) setFieldError(null);
  };

  return (
    <>
      <SEOHead
        title="Contact Milky Way Planner — Questions, Feedback & Support"
        description="Get in touch with Milky Way Planner. Questions about astrophotography planning, feature requests, premium support, and feedback welcome."
        keywords="contact milky way planner, astrophotography support, milky way planner help, astrophotography questions"
        canonicalUrl="/contact"
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Contact</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Get in Touch
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Questions, feedback, feature requests, or just want to say hi. I'd love to hear from you.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot — hidden from users, bots fill it */}
                <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" tabIndex={-1}>
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    autoComplete="off"
                    tabIndex={-1}
                  />
                </div>
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    maxLength={CONTACT_LIMITS.name}
                    aria-invalid={errorFor('name') ? true : undefined}
                    aria-describedby={errorFor('name') ? 'name-error' : undefined}
                    className="mt-1.5"
                    placeholder="Your name"
                  />
                  <FieldError id="name-error" message={errorFor('name')} />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    maxLength={EMAIL_MAX}
                    aria-invalid={errorFor('email') ? true : undefined}
                    aria-describedby={errorFor('email') ? 'email-error' : undefined}
                    className="mt-1.5"
                    placeholder="your.email@example.com"
                  />
                  <FieldError id="email-error" message={errorFor('email')} />
                </div>

                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    maxLength={CONTACT_LIMITS.subject}
                    aria-invalid={errorFor('subject') ? true : undefined}
                    aria-describedby={errorFor('subject') ? 'subject-error' : undefined}
                    className="mt-1.5"
                    placeholder="What is this about?"
                  />
                  <FieldError id="subject-error" message={errorFor('subject')} />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={CONTACT_LIMITS.message}
                    aria-invalid={errorFor('message') ? true : undefined}
                    aria-describedby={errorFor('message') ? 'message-error message-count' : 'message-count'}
                    className="mt-1.5 min-h-32"
                    placeholder="Tell me more..."
                  />
                  <FieldError id="message-error" message={errorFor('message')} />
                  <p id="message-count" className={`mt-1.5 text-xs ${messageAtLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {messageAtLimit
                      ? `Messages are limited to ${CONTACT_LIMITS.message.toLocaleString('en-US')} characters.`
                      : `${formData.message.length.toLocaleString('en-US')} / ${CONTACT_LIMITS.message.toLocaleString('en-US')} characters`}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 lg:border-l lg:border-border lg:pl-10 space-y-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Email</h3>
                <p className="text-muted-foreground">eric@milkywayplanner.com</p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Response time</h3>
                <p className="text-muted-foreground">Usually within 24 hours.</p>
              </div>

              <div className="border-t border-border pt-6">
                <h3 className="font-semibold text-foreground mb-3">Common questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground text-sm mb-1">How accurate are the visibility predictions?</h4>
                    <p className="text-muted-foreground text-sm">Based on astronomical algorithms, accurate to within minutes for timing and degrees for positioning.</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground text-sm mb-1">Can I use this for astrophotography planning?</h4>
                    <p className="text-muted-foreground text-sm">That's exactly what it's built for. Precise timing for optimal shooting conditions.</p>
                  </div>

                  {(!user || !isSubscribed) && (
                    <div>
                      <h4 className="font-medium text-foreground text-sm mb-1">What's included in Premium?</h4>
                      <p className="text-muted-foreground text-sm">Moon Planner, Trip Planner, unlimited saved locations, and exports to Excel, CSV, and calendar formats.</p>
                    </div>
                  )}
                </div>
              </div>

              {(!user || !isSubscribed) && (
                <div className="border-t border-border pt-6">
                  <Button
                    variant="premium"
                    onClick={handlePremiumUpgrade}
                    disabled={isUpgrading}
                    className="w-full"
                  >
                    {isUpgrading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Opening checkout…
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Get Premium
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
